import { createContext, useContext, useState, useEffect, useCallback } from "react";
import type { ReactNode } from "react";
import { fetchProfile } from "@/services/profile.service";

interface PlanContextValue {
  isPro: boolean;
  planLoading: boolean;
  refreshPlan: () => void;
}

const PlanContext = createContext<PlanContextValue | null>(null);

export function PlanProvider({ children }: { children: ReactNode }) {
  // null = no backend (mock mode) → no restrictions
  const [plan, setPlan] = useState<"free" | "pro" | null>(null);
  const [planLoading, setPlanLoading] = useState(true);

  const refresh = useCallback(() => {
    setPlanLoading(true);
    fetchProfile()
      .then((p) => setPlan(p?.plan ?? null))
      .catch(() => setPlan(null))
      .finally(() => setPlanLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // null means no backend → treat as pro (unrestricted in mock/demo mode)
  const isPro = plan === "pro" || plan === null;

  return (
    <PlanContext.Provider value={{ isPro, planLoading, refreshPlan: refresh }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const ctx = useContext(PlanContext);
  if (!ctx) throw new Error("usePlan must be used within PlanProvider");
  return ctx;
}
